import { ChevronDown, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { Button } from '@/components/ui/button'
import { isOwningSourceId } from '@/lib/problems/attribution'
import { AddSourceModal } from '../add-source-modal'
import type { ProblemEditorData } from '../problem-editor'
import type { ProblemSource, Source } from '../types'
import { SortableSourceCard } from './sortable-source-card'
import { resolveOriginalSourceId } from './source-candidates'
import type { SavableSourceField } from './sources-section'

interface AvailableSource {
  name: string
  icon: string | null
}

export interface ProblemSourcesManagerProps {
  formData: ProblemEditorData
  sources: ProblemSource[]
  availableSourcesToAdd: AvailableSource[]
  loadingAvailableSources: boolean
  savingSource: boolean
  expandedSourceId: string | null
  setExpandedSourceId: (id: string | null) => void
  onAddSource: (sourceName: string, icon: string | null) => Promise<void>
  onDeleteSource: (sourceId: string) => Promise<void>
  /**
   * Generic in the field so the key and the value stay correlated: the flat
   * `string | boolean | null` this replaced let `('isOriginal', 'yes')` and
   * `('order', null)` compile, and the value is spread straight onto the row.
   * Restricted to the savable fields because every local edit here is on its
   * way to a save — see `SavableSourceField`.
   */
  onLocalSourceUpdate: <K extends SavableSourceField>(
    sourceId: string,
    field: K,
    value: ProblemSource[K],
  ) => void
  /**
   * Apply, save, and — if the save is rejected — undo a field edit, as one
   * step. The immediate-save toggles use this instead of pairing
   * `onLocalSourceUpdate` with `onSaveSourceField`: marking an original also
   * clears whatever row held the mark, and only the owner of the list can put
   * that row back.
   */
  onCommitSourceField: <K extends SavableSourceField>(
    sourceId: string,
    field: K,
    value: ProblemSource[K],
  ) => Promise<void>
  /**
   * Save a field that only concerns its own row.
   *
   * `isOriginal` is excluded rather than documented: the two save props are
   * otherwise interchangeable at the call site, and picking this one for the
   * original toggle would save the mark without clearing the row that held it
   * — a compile error is the only thing that reliably stops that.
   */
  onSaveSourceField: <K extends Exclude<SavableSourceField, 'isOriginal'>>(
    sourceId: string,
    field: K,
    value: ProblemSource[K],
  ) => Promise<void>
  onSourceCreated: (source: Source) => void
}

export function ProblemSourcesManager({
  formData,
  sources,
  availableSourcesToAdd,
  loadingAvailableSources,
  savingSource,
  expandedSourceId,
  setExpandedSourceId,
  onAddSource,
  onDeleteSource,
  onLocalSourceUpdate,
  onCommitSourceField,
  onSaveSourceField,
  onSourceCreated,
}: ProblemSourcesManagerProps) {
  const [addSourceDropdownOpen, setAddSourceDropdownOpen] = useState(false)
  const [addSourceModalOpen, setAddSourceModalOpen] = useState(false)

  const orderedSources = useMemo(
    () => [...sources].sort((a, b) => a.order - b.order),
    [sources],
  )
  const owningSourceId = orderedSources.find((source) =>
    isOwningSourceId(source.id),
  )?.id
  // The answer the learner will see, resolved through the shared ladder rather
  // than read off each row's column — otherwise this tab and the Statement
  // tab's preview disagree about who is credited, and an admin trusting this
  // one marks a second source and silently changes the widget.
  const resolvedOriginalId = useMemo(
    () => resolveOriginalSourceId(orderedSources),
    [orderedSources],
  )

  const handleAddSourceClick = async (
    sourceName: string,
    icon: string | null,
  ) => {
    await onAddSource(sourceName, icon)
    setAddSourceDropdownOpen(false)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Problem Sources</h3>
          <p className="text-muted-foreground text-sm">
            The marked source owns identity; additional sources are attribution.
          </p>
        </div>
        <div className="relative">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddSourceDropdownOpen(!addSourceDropdownOpen)}
            disabled={savingSource || loadingAvailableSources}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Source
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
          {addSourceDropdownOpen && (
            <div className="bg-popover border-border absolute top-full right-0 z-10 mt-1 w-64 rounded-md border py-1 shadow-lg">
              {availableSourcesToAdd.length > 0 && (
                <>
                  {availableSourcesToAdd.map((source) => (
                    <button
                      key={source.name}
                      type="button"
                      onClick={() =>
                        handleAddSourceClick(source.name, source.icon)
                      }
                      className="hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                    >
                      <AuthoredIcon icon={source.icon} size="sm" fallbackChip />
                      {source.name}
                    </button>
                  ))}
                  <div className="border-border my-1 border-t" />
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  setAddSourceDropdownOpen(false)
                  setAddSourceModalOpen(true)
                }}
                className="hover:bg-muted text-primary flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Add New Source...
              </button>
            </div>
          )}
        </div>
      </div>

      {sources.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground text-sm">
            No sources configured. Click "Add Source" to add platforms.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {orderedSources.map((source) => (
            <SortableSourceCard
              key={source.id}
              source={source}
              isPrimary={source.id === owningSourceId}
              isResolvedOriginal={source.id === resolvedOriginalId}
              isExpanded={expandedSourceId === source.id}
              onToggleExpand={() =>
                setExpandedSourceId(
                  expandedSourceId === source.id ? null : source.id,
                )
              }
              formData={formData}
              onLocalSourceUpdate={onLocalSourceUpdate}
              onCommitSourceField={onCommitSourceField}
              onSaveSourceField={onSaveSourceField}
              onDeleteSource={onDeleteSource}
            />
          ))}
        </div>
      )}

      {/* Add Source Modal */}
      <AddSourceModal
        isOpen={addSourceModalOpen}
        onClose={() => setAddSourceModalOpen(false)}
        onSourceCreated={onSourceCreated}
      />
    </div>
  )
}
